import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Badge,
  Spinner,
  Alert,
  ListGroup,
  Accordion,
  Table,
  ProgressBar,
  Dropdown,
} from 'react-bootstrap';
import {
  FaArrowLeft,
  FaClipboardCheck,
  FaCheckCircle,
  FaExclamationTriangle,
  FaClock,
  FaTools,
  FaUser,
  FaCalendar,
  FaFileAlt,
  FaSync,
  FaBuilding,
  FaChartLine,
  FaInfoCircle,
  FaEdit,
  FaTrash,
  FaFilter,
  FaSearch,
  FaListAlt,
  FaHammer,
  FaPaintRoller,
  FaWrench,
  FaHome,
} from 'react-icons/fa';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const API_BASE = 'http://34.206.193.218:4001/api/admin';

const PunchList = () => {
  const { taskId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [punchLists, setPunchLists] = useState([]);
  // Removed bidTaskInfo state since we can't fetch it
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeAccordionKey, setActiveAccordionKey] = useState('0');
  
  const token = localStorage.getItem('adminToken') || localStorage.getItem('token');

  /* ================= FETCH PUNCH LISTS ================= */
  const fetchPunchLists = async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${API_BASE}/data/getPunchListByBidTask/${taskId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (res.data.success) {
        setPunchLists(res.data.punchLists || []);
        // Removed bid task info extraction since we can't fetch it
      }
    } catch (error) {
      console.error('Fetch punch lists error:', error);
      toast.error('Failed to load punch lists');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (taskId) {
      fetchPunchLists();
    }
  }, [taskId]);

  /* ================= FORMATTERS ================= */
  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'Not set';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  /* ================= STATUS & TYPE HANDLERS ================= */
  const getStatusBadge = (status) => {
    switch (status) {
      case 'COMPLETED':
        return <Badge bg="success" className="px-3 py-2">Completed</Badge>;
      case 'IN_PROGRESS':
        return <Badge bg="info" className="px-3 py-2">In Progress</Badge>;
      case 'PENDING':
        return <Badge bg="warning" className="px-3 py-2" text="dark">Pending</Badge>;
      case 'OVERDUE':
        return <Badge bg="danger" className="px-3 py-2">Overdue</Badge>;
      default:
        return <Badge bg="secondary" className="px-3 py-2">{status}</Badge>;
    }
  };

  const getTypeBadge = (type) => {
    switch (type) {
      case 'INTERIOR':
        return <Badge bg="primary" className="px-3 py-2">Interior</Badge>;
      case 'EXTERIOR':
        return <Badge bg="secondary" className="px-3 py-2">Exterior</Badge>;
      case 'STRUCTURAL':
        return <Badge bg="dark" className="px-3 py-2">Structural</Badge>;
      case 'FINISHING':
        return <Badge bg="info" className="px-3 py-2">Finishing</Badge>;
      case 'MECHANICAL':
        return <Badge bg="warning" className="px-3 py-2" text="dark">Mechanical</Badge>;
      case 'ELECTRICAL':
        return <Badge bg="danger" className="px-3 py-2">Electrical</Badge>;
      default:
        return <Badge bg="light" text="dark" className="px-3 py-2">{type}</Badge>;
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'INTERIOR':
        return <FaHome className="text-primary" />;
      case 'EXTERIOR':
        return <FaBuilding className="text-secondary" />;
      case 'STRUCTURAL':
        return <FaHammer className="text-dark" />;
      case 'FINISHING':
        return <FaPaintRoller className="text-info" />;
      case 'MECHANICAL':
        return <FaWrench className="text-warning" />;
      case 'ELECTRICAL':
        return <FaTools className="text-danger" />;
      default:
        return <FaTools className="text-muted" />;
    }
  };

  /* ================= FILTER & SEARCH ================= */
  const filteredPunchLists = punchLists.filter(punch => {
    // Filter by status
    if (filterStatus !== 'all' && punch.status !== filterStatus) {
      return false;
    }
    
    // Filter by type
    if (filterType !== 'all' && punch.todoType !== filterType) {
      return false;
    }
    
    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      return (
        punch.taskName?.toLowerCase().includes(searchLower) ||
        punch.description?.toLowerCase().includes(searchLower) ||
        punch.todoType?.toLowerCase().includes(searchLower) ||
        punch.status?.toLowerCase().includes(searchLower)
      );
    }
    
    return true;
  });

  /* ================= CALCULATIONS ================= */
  const calculateStats = () => {
    const total = punchLists.length;
    const completed = punchLists.filter(p => p.status === 'COMPLETED').length;
    const pending = punchLists.filter(p => p.status === 'PENDING').length;
    const inProgress = punchLists.filter(p => p.status === 'IN_PROGRESS').length;
    const overdue = punchLists.filter(p => p.status === 'OVERDUE').length;

    return { total, completed, pending, inProgress, overdue };
  };

  const calculateProgress = () => {
    const stats = calculateStats();
    if (stats.total === 0) return 0;
    return Math.round((stats.completed / stats.total) * 100);
  };

  /* ================= ACCORDION HANDLER ================= */
  const handleAccordionToggle = (eventKey) => {
    setActiveAccordionKey(activeAccordionKey === eventKey ? null : eventKey);
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <Spinner animation="border" variant="primary" />
        <span className="ms-3">Loading punch lists...</span>
      </div>
    );
  }

  const stats = calculateStats();
  const progress = calculateProgress();

  return (
    <Container fluid className="py-4" style={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      <ToastContainer position="top-right" autoClose={3000} />

      {/* HEADER */}
      <Card className="border-0 shadow-sm mb-4">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-start mb-4">
            <div>
              <Button
                variant="outline-primary"
                onClick={() => navigate(-1)}
                className="mb-3 d-flex align-items-center"
              >
                <FaArrowLeft className="me-2" />
                Back to Bid Estimates
              </Button>
              <h2 className="fw-bold mb-2">
                <FaClipboardCheck className="me-3" />
                Punch List Management
              </h2>
              
              {/* Simplified header without bidTaskInfo */}
              <div className="mb-3">
                <h4 className="mb-1">
                  <FaListAlt className="me-2 text-primary" />
                  Task ID: {taskId?.substring(0, 16)}...
                </h4>
                <div className="d-flex flex-wrap gap-3 align-items-center">
                  <Badge bg="info" className="px-3 py-2">
                    <FaClipboardCheck className="me-2" />
                    Punch List Items
                  </Badge>
                  <Badge bg="secondary" className="px-3 py-2">
                    <FaFileAlt className="me-2" />
                    Task ID: {taskId?.substring(0, 12)}...
                  </Badge>
                </div>
              </div>

              <div className="d-flex align-items-center">
                <div className="me-4">
                  <span className="text-muted me-2">Completion:</span>
                  <Badge bg={progress === 100 ? 'success' : progress >= 50 ? 'info' : 'warning'}>
                    {progress}%
                  </Badge>
                </div>
                <div>
                  <span className="text-muted me-2">Items:</span>
                  <Badge bg="dark">{punchLists.length}</Badge>
                </div>
              </div>
            </div>
            
            <div className="d-flex gap-2">
              <Button
                variant="primary"
                onClick={fetchPunchLists}
                className="d-flex align-items-center"
              >
                <FaSync className="me-2" />
                Refresh
              </Button>
            </div>
          </div>

          {/* PROGRESS BAR */}
          <div className="mb-4">
            <div className="d-flex justify-content-between mb-1">
              <small className="text-muted">Punch List Completion</small>
              <small className="text-muted">{progress}%</small>
            </div>
            <ProgressBar 
              now={progress} 
              variant={progress === 100 ? 'success' : progress >= 50 ? 'info' : 'warning'}
              style={{ height: '8px' }}
            />
          </div>
        </Card.Body>
      </Card>

      {/* STATISTICS */}
      {punchLists.length > 0 && (
        <Card className="border-0 shadow-sm mb-4">
          <Card.Header className="bg-light">
            <h4 className="mb-0">
              <FaChartLine className="me-2" />
              Punch List Overview
            </h4>
          </Card.Header>
          <Card.Body>
            <Row className="text-center">
              <Col md={3} sm={6} className="mb-4">
                <Card className="border-0 bg-primary bg-opacity-10 h-100">
                  <Card.Body>
                    <div className="fw-bold display-6 mb-2">{stats.total}</div>
                    <div className="text-muted">Total Items</div>
                  </Card.Body>
                </Card>
              </Col>
              
              <Col md={2} sm={4} className="mb-4">
                <Card className="border-0 bg-success bg-opacity-10 h-100">
                  <Card.Body>
                    <div className="fw-bold display-6 mb-2 text-success">{stats.completed}</div>
                    <div className="text-muted">Completed</div>
                  </Card.Body>
                </Card>
              </Col>
              
              <Col md={2} sm={4} className="mb-4">
                <Card className="border-0 bg-info bg-opacity-10 h-100">
                  <Card.Body>
                    <div className="fw-bold display-6 mb-2 text-info">{stats.inProgress}</div>
                    <div className="text-muted">In Progress</div>
                  </Card.Body>
                </Card>
              </Col>
              
              <Col md={2} sm={4} className="mb-4">
                <Card className="border-0 bg-warning bg-opacity-10 h-100">
                  <Card.Body>
                    <div className="fw-bold display-6 mb-2 text-warning">{stats.pending}</div>
                    <div className="text-muted">Pending</div>
                  </Card.Body>
                </Card>
              </Col>
              
              <Col md={3} sm={6} className="mb-4">
                <Card className="border-0 bg-danger bg-opacity-10 h-100">
                  <Card.Body>
                    <div className="fw-bold display-6 mb-2 text-danger">{stats.overdue}</div>
                    <div className="text-muted">Overdue</div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}

      {/* PUNCH LIST ITEMS */}
      {filteredPunchLists.length === 0 ? (
        <Card className="border-0 shadow-sm">
          <Card.Body className="text-center py-5">
            <FaClipboardCheck size={64} className="text-muted mb-3" />
            <h4 className="mb-3">No Punch List Items Found</h4>
            <p className="text-muted mb-4">
              {searchTerm || filterStatus !== 'all' || filterType !== 'all'
                ? 'No punch list items match your search criteria'
                : 'No punch list items have been added to this bid task yet.'}
            </p>
            {(searchTerm || filterStatus !== 'all' || filterType !== 'all') && (
              <Button 
                variant="outline-primary" 
                onClick={() => {
                  setSearchTerm('');
                  setFilterStatus('all');
                  setFilterType('all');
                }}
              >
                Clear Filters
              </Button>
            )}
          </Card.Body>
        </Card>
      ) : (
        <Accordion activeKey={activeAccordionKey} onSelect={handleAccordionToggle} className="mb-4">
          {filteredPunchLists.map((punch, index) => (
            <Accordion.Item key={punch._id} eventKey={index.toString()}>
              <Accordion.Header>
                <div className="d-flex w-100 align-items-center">
                  <div className="me-3">
                    <Badge bg="light" text="dark" className="px-2 py-1">
                      {index + 1}
                    </Badge>
                  </div>
                  <div className="flex-grow-1">
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="fw-bold fs-5">{punch.taskName}</div>
                      <div className="d-flex gap-2">
                        {getTypeBadge(punch.todoType)}
                        {getStatusBadge(punch.status)}
                      </div>
                    </div>
                    <div className="small text-muted">
                      <FaClock className="me-1" />
                      Created: {formatDateTime(punch.createdAt)} by {punch.createdBy?.name}
                    </div>
                  </div>
                </div>
              </Accordion.Header>
              
              <Accordion.Body>
                <Row>
                  {/* Left Column - Details */}
                  <Col md={8}>
                    <Card className="h-100">
                      <Card.Header className="bg-light">
                        <h5 className="mb-0">
                          <FaFileAlt className="me-2" />
                          Task Details
                        </h5>
                      </Card.Header>
                      <Card.Body>
                        <div className="mb-4">
                          <h6 className="text-muted mb-2">Description</h6>
                          <div className="bg-light p-3 rounded border">
                            <p className="mb-0">{punch.description || 'No description provided'}</p>
                          </div>
                        </div>

                        <Row>
                          <Col md={6}>
                            <div className="mb-3">
                              <h6 className="text-muted mb-2">
                                <FaTools className="me-2" />
                                Task Information
                              </h6>
                              <ListGroup variant="flush">
                                <ListGroup.Item className="border-0 px-0 py-2">
                                  <div className="d-flex justify-content-between">
                                    <small className="text-muted">Task Type:</small>
                                    <div className="d-flex align-items-center">
                                      {getTypeIcon(punch.todoType)}
                                      <span className="ms-2 fw-medium">{punch.todoType}</span>
                                    </div>
                                  </div>
                                </ListGroup.Item>
                                <ListGroup.Item className="border-0 px-0 py-2">
                                  <div className="d-flex justify-content-between">
                                    <small className="text-muted">Status:</small>
                                    {getStatusBadge(punch.status)}
                                  </div>
                                </ListGroup.Item>
                                <ListGroup.Item className="border-0 px-0 py-2">
                                  <div className="d-flex justify-content-between">
                                    <small className="text-muted">Punch List ID:</small>
                                    <span className="small text-muted">{punch._id?.substring(0, 12)}...</span>
                                  </div>
                                </ListGroup.Item>
                              </ListGroup>
                            </div>
                          </Col>
                          
                          <Col md={6}>
                            <div className="mb-3">
                              <h6 className="text-muted mb-2">
                                <FaCalendar className="me-2" />
                                Timeline
                              </h6>
                              <ListGroup variant="flush">
                                <ListGroup.Item className="border-0 px-0 py-2">
                                  <div className="d-flex justify-content-between">
                                    <small className="text-muted">Created:</small>
                                    <span className="small">{formatDateTime(punch.createdAt)}</span>
                                  </div>
                                </ListGroup.Item>
                                <ListGroup.Item className="border-0 px-0 py-2">
                                  <div className="d-flex justify-content-between">
                                    <small className="text-muted">Last Updated:</small>
                                    <span className="small">{formatDateTime(punch.updatedAt)}</span>
                                  </div>
                                </ListGroup.Item>
                              </ListGroup>
                            </div>
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>
                  </Col>

                  {/* Right Column - Created Info */}
                  <Col md={4}>
                    <Card className="h-100">
                      <Card.Header className="bg-light">
                        <h5 className="mb-0">
                          <FaUser className="me-2" />
                          Created Information
                        </h5>
                      </Card.Header>
                      <Card.Body>
                        <div className="text-center mb-4">
                          <div className="bg-primary bg-opacity-10 rounded-circle d-inline-flex p-4 mb-3">
                            <FaUser size={32} className="text-primary" />
                          </div>
                          <h5 className="mb-1">{punch.createdBy?.name || 'Unknown'}</h5>
                          <p className="text-muted small mb-3">{punch.createdBy?.email || 'No email provided'}</p>
                          
                          <Badge bg="light" text="dark" className="px-3 py-2">
                            <FaUser className="me-2" />
                            Creator
                          </Badge>
                        </div>

                        <div className="mt-4">
                          <h6 className="text-muted mb-3">Activity Log</h6>
                          <ListGroup variant="flush">
                            <ListGroup.Item className="border-0 px-0 py-2">
                              <div className="d-flex align-items-center">
                                <FaCheckCircle className="text-success me-3" />
                                <div>
                                  <div className="small">Item Created</div>
                                  <small className="text-muted">{formatDateTime(punch.createdAt)}</small>
                                </div>
                              </div>
                            </ListGroup.Item>
                            {punch.status === 'COMPLETED' && (
                              <ListGroup.Item className="border-0 px-0 py-2">
                                <div className="d-flex align-items-center">
                                  <FaCheckCircle className="text-success me-3" />
                                  <div>
                                    <div className="small">Marked as Completed</div>
                                    <small className="text-muted">{formatDateTime(punch.updatedAt)}</small>
                                  </div>
                                </div>
                              </ListGroup.Item>
                            )}
                            {punch.status === 'IN_PROGRESS' && (
                              <ListGroup.Item className="border-0 px-0 py-2">
                                <div className="d-flex align-items-center">
                                  <FaClock className="text-info me-3" />
                                  <div>
                                    <div className="small">Work Started</div>
                                    <small className="text-muted">{formatDateTime(punch.updatedAt)}</small>
                                  </div>
                                </div>
                              </ListGroup.Item>
                            )}
                          </ListGroup>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              </Accordion.Body>
            </Accordion.Item>
          ))}
        </Accordion>
      )}

      {/* SUMMARY FOOTER */}
      {filteredPunchLists.length > 0 && (
        <Card className="border-0 shadow-sm">
          <Card.Body className="bg-light">
            <Row className="align-items-center">
              <Col md={4}>
                <div className="small text-muted">
                  Showing {filteredPunchLists.length} of {punchLists.length} items
                  {searchTerm && ` matching "${searchTerm}"`}
                  {(filterStatus !== 'all' || filterType !== 'all') && ' (filtered)'}
                </div>
              </Col>
              <Col md={8}>
                <div className="d-flex justify-content-end gap-4">
                  <div className="text-center">
                    <div className="fw-bold text-primary">{stats.total}</div>
                    <small className="text-muted">Total Items</small>
                  </div>
                  <div className="text-center">
                    <div className="fw-bold text-success">{stats.completed}</div>
                    <small className="text-muted">Completed</small>
                  </div>
                  <div className="text-center">
                    <div className="fw-bold text-info">{stats.inProgress}</div>
                    <small className="text-muted">In Progress</small>
                  </div>
                  <div className="text-center">
                    <div className="fw-bold text-warning">{stats.pending}</div>
                    <small className="text-muted">Pending</small>
                  </div>
                  <div className="text-center">
                    <div className={`fw-bold ${progress === 100 ? 'text-success' : progress >= 50 ? 'text-info' : 'text-warning'}`}>
                      {progress}%
                    </div>
                    <small className="text-muted">Completion Rate</small>
                  </div>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}

      {/* EMPTY STATE INFO */}
      {punchLists.length === 0 && (
        <Card className="border-0 shadow-sm mt-4">
          <Card.Body>
            <Alert variant="info" className="mb-0">
              <div className="d-flex align-items-center">
                <FaInfoCircle className="me-3" size={24} />
                <div>
                  <h5 className="alert-heading mb-2">Punch List Management</h5>
                  <p className="mb-0">
                    This page displays all punch list items associated with the selected bid task.
                    Punch lists track pending tasks, quality checks, and completion requirements
                    that need to be addressed before final project handover.
                  </p>
                </div>
              </div>
            </Alert>
          </Card.Body>
        </Card>
      )}
    </Container>
  );
};

export default PunchList;